import React, { useEffect, useRef, useState } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { Collapse, Dropdown, Menu, Tooltip, Tag, Space, Row, Col, Divider, Select, Button, message } from 'antd';
import { UserOutlined, DownOutlined, ClockCircleOutlined, ReadOutlined, FileImageOutlined, EyeOutlined, EditOutlined, DeleteOutlined, NotificationOutlined, UserSwitchOutlined } from '@ant-design/icons';
import './List.less';
import { Project, projectPageModel } from '../model';
import TextArea from 'antd/lib/input/TextArea';  
import moment from 'moment'; 
import AddEdit from './AddEdit';
import UserIcon from '@/components/UserIcon/UserIcon';
import { CommentAE } from '@/common/model';
import { projectApi } from '../ProjectApi';
import { ctrl } from '../controller';
import { formatTimeDifference, hexFromStr, invertColor } from '@/utils/S';
import CommentList from '@/delivery/components/CommentList'; 
import DeliveryList from './DeliveryList';
import { globalStore } from '@/common';
import StatusSelect from '@/components/StatusSelect/StatusSelect';


const { Option } = Select
interface ListProps {
  list: Project[];
}

const List: React.FC<ListProps> = observer(({ list }) => {

  const statusList = ['待开始','进行中','已完成','已废弃'];

  const renderHeader = (project: Project) => {
    const title = project.name?.trim() || '未命名项目'
    const titleColor = hexFromStr(title)
    return (
    <Row className="list-item-header" align="middle" key={project.id}>
      <Col className="left" span={12}>
        <span
          className="project-business-type"
          title={title}
          style={{ backgroundColor: titleColor, color: invertColor(titleColor) }}
        >
          {title}
        </span>
        <span className="description">{project.content}</span>
        {
          project.deliveries && project.deliveries.length > 0?(
            <span className='delivery-list-btn' onClick={(e) => { e.stopPropagation();  handleShowDeliveries(project); }}> 共{project.deliveries.length}个交付</span>
          ):''
        }
        
      </Col>
      <Col className="right" span={10}>
        <Space size={10}>
          <Tooltip title={project.submitterName}>
               <UserIcon effect='post' id={project.submitterId+''}></UserIcon>
          </Tooltip>
          <Divider type="vertical" />
          <Tooltip title={project.directorName}>
               <UserIcon effect='leader' id={project.directorId+''}></UserIcon>
          </Tooltip>
          <div onClick={(e) => e.stopPropagation()} className='status-select'>
                  {/* 状态快捷选择 */}
                  <StatusSelect 
                    placeholder=''
                    width={120}
                    value={project.status as number}
                    valueList={[0,1,2,3]}
                    statusList={['待开始','进行中','已完成','已废弃']}
                    colorList={['#248dea','#df6005','#0ea913','#979797']}
                    onChange={(val) => { 
                      ctrl.changeStatus(project.id as number, val); 
                    }} 
                  /> 
          </div>
          
        </Space>
      </Col>
    </Row>
    )
  }

  const inputRef = useRef<HTMLInputElement>()

  useEffect((): (() => void) => {
    const handleKeyDown = (e) => {
      e.stopPropagation()

      if (e.keyCode === 13 && e.ctrlKey) {
        inputRef.current.blur()
        handleComment()
      }
    }

    document.addEventListener('keydown', handleKeyDown, false)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, false)
    }
  }, [])

  const [comment, setComment] = useState('')

  const [isFocus, setFocus] = useState(false)

  const handleCommentChange = (e) => {
    setComment(e.target.value)
  }

  const handleFocus = () => {
     setFocus(true);
  }

  const handleBlur = () => {
    setFocus(false);
  }
  
  const handleComment = async () => {
    const commentAE : CommentAE = new CommentAE();
    commentAE.content = comment 
    await projectApi.addOrEditComment(commentAE); 
  }

  const [showAdd, setShowAdd] = useState(false)

  const getStatusColor = (status: number) => {
    switch (status) {
      case 0: return 'blue'; // 待开始
      case 1: return 'rgb(249, 105, 9)';//进行中
      case 2: return 'green'; // 已完成
      case 3: return 'grey'; // 已废弃
      default: return 'white';
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 0: return '待开始';
      case 1: return '进行中';
      case 2: return '已完成';
      case 3: return '已终止';
      default: return '未知';
    }
  };

  const store = useLocalObservable(() => ({
    activeKey: [] as any
  }))
  useEffect(() => {
    // @ts-ignore
    window.onGlobalEvent('bugFolderAll', () => {
      store.activeKey = []
    })
    return () => {
      // @ts-ignore
      window.offGlobalEvent('bugFolderAll')
    }
  }, [])
  
  const getStatusMenu = (demand: Project) => (
    <Menu>
      <Menu.Item key="0">待开始</Menu.Item>
      <Menu.Item key="1">进行中</Menu.Item>
      <Menu.Item key="1">已完成</Menu.Item>
      <Menu.Item key="2">已废弃</Menu.Item>
    </Menu>
  );  

  const [selectedProject, setSelectedProject] = useState<Project | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeliveryModalVisible, setIsDeliveryModalVisible] = useState(false);

 
  const handleEdit = (project: Project) => {
    setSelectedProject(project);
    setIsModalVisible(true);
  };
  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleShowDeliveries = (project: Project) => {
    setSelectedProject(project);
    setIsDeliveryModalVisible(true);
  };

  const handleCloseDeliveries = () => { 
    setIsDeliveryModalVisible(false);
  };

 



  return (
    <Collapse
      activeKey={store.activeKey}
      onChange={(e) => (store.activeKey = e)}
    >
      {list.map((project,index) => (
        <Collapse.Panel showArrow={false} header={renderHeader(project)} key={index}>
          <Space className="details w-full">
            <Row>
              <Col span={12} className='icon-info-list-container'>
                    <Row className='first-row info-row'>
                          <Col span={1}></Col>
                          <Col span={2}>  
                              <ClockCircleOutlined  />
                          </Col>
                          <Col span={21} style={{paddingRight:20}}>
                              <span className="">{moment(project.submitTime).format('YYYY年MM月DD日 HH:mm')} 截止</span>
                          </Col>
                    </Row>
                    <Row className=' info-row'>
                          <Col span={1}></Col>
                          <Col span={2}>  
                              <ReadOutlined  />
                          </Col>
                          <Col span={21} style={{paddingRight:20}}>
                               <span style={{ whiteSpace: 'pre-wrap' }}>{project.content}</span>
                          </Col>
                    </Row>
                    <Row className=' info-row'>
                          <Col span={1}></Col>
                          <Col span={2}>  
                             <UserSwitchOutlined />
                          </Col>
                          <Col span={21} style={{paddingRight:20}}>
                               <span style={{ whiteSpace: 'pre-wrap' }}>
                                  <UserIcon showName effect='post' id={project.submitterId+''}></UserIcon>
                               </span>
                          </Col>
                    </Row> 
                    <Row className=' info-row'>
                          <Col span={1}></Col>
                          <Col span={2}>  
                              <UserOutlined />
                          </Col>
                          <Col span={21} style={{paddingRight:20}}>
                               <span style={{ whiteSpace: 'pre-wrap' }}>
                                  <UserIcon showName effect='leader' id={project.directorId+''}></UserIcon>
                               </span>
                          </Col>
                    </Row> 
                    <Row className='last-row info-row'>
                          <Col span={1}></Col>
                          <Col span={2}>  
                              <EyeOutlined  />
                          </Col>
                          <Col span={21} className='event-list'>
                              {
                                // 操作日志
                                project.logs.map( (operation)=>(
                                      <div key ={operation.id}> 
                                        <span className='name-hint'>{operation.operatorId?operation.operatorName:'未知'} </span>
                                        <span>{operation.content}</span>
                                        <span className='time-desc'>{operation.logTime?formatTimeDifference(operation.logTime):''} </span>
                                      </div> 
                                  )
                                )
                              } 
                          </Col>
                    </Row> 
              </Col>
              <Col span={4} className='edit-operation-area-middle'>
                    <Space size={0}>
                            <Button
                              type="link"
                              icon={<EditOutlined />}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEdit(project)
                              }}
                            >
                              编辑
                            </Button>
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={(e) => {
                                e.stopPropagation()
                                ctrl.deleteProject(project.id)
                              }}
                            >
                              删除
                            </Button>
                    </Space>
              </Col>
              <Col span={8}>
                 <div className="flex flex-col" style={{ padding: '0 20px' }}>
                    <TextArea
                        allowClear
                        ref={inputRef}
                        autoSize={{ minRows: 1, maxRows:2 }}
                        placeholder="有什么想说的吗？"
                        value={ comment}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onChange={handleCommentChange}
                        className='comment-content-input'
                        
                      />
                    <div style={{textAlign:'left',fontSize:13, paddingBlock:12}}>评论({project.comments.length})</div>
                    <CommentList oldDataSource={null} newDataSource={project.comments} />
                  </div>
              </Col>
            </Row>
           
           
          </Space>
        </Collapse.Panel>
      ))}
       <AddEdit
          visible={isModalVisible}
          onClose={handleCloseModal}
          project={selectedProject}
          candidateDemands={projectPageModel.demandList}
       />
       <DeliveryList
          visible={isDeliveryModalVisible}
          onClose={handleCloseDeliveries}
          project={selectedProject}
        />
    </Collapse>
  );
});

export default List;